import { ChartData } from 'src/atoms/buildout'
import { DemoDatum } from 'src/common-services'
import { NATIONAL_AVG_MAPPINGS } from 'src/components/demo-table/constants'
import { DemoTableOptions } from 'src/components/demo-table/table/toolbar'
import { TableFormat } from 'src/components/insights/true-trade-area/overview/constants'
import {
  NationalAvgDataType,
  getNationalAverageData,
  getNationalAvgInArea,
  getNationalAvgInRadius,
} from 'src/utils/demographics'

export const percentUnit = (columns: Array<Record<string, Record<string, number>>>, format: TableFormat) =>
  columns.flatMap(
    (column) =>
      column &&
      Object.values(column).map((subColumn) => {
        if (format.totalRow) {
          return 100 / subColumn[format.totalRow]
        }
        const rowKeys = Object.keys(subColumn)
        const total = Object.values(subColumn)
          .filter((_, index) => {
            const row = rowKeys[index]
            return format.breakdownRows?.includes(row)
          })
          .reduce((prev, curr) => {
            const c = Number(curr)
            if (isNaN(c)) {
              return prev + 0
            }
            return prev + Number(curr)
          }, 0)
        return 100 / total
      })
  )

type FormatChartDataParams = {
  currentDataPoint: string
  dataPoint:
    | Record<string, { currentYear?: number | Record<string, number>; fiveYear?: number | Record<string, number> }>
    | undefined
  tableOptions: DemoTableOptions
  percentUnit: number
  format: TableFormat
  radius?: number
  area?: number
  dataType?: NationalAvgDataType
}
export const formatChartData = ({
  currentDataPoint,
  dataPoint,
  tableOptions,
  percentUnit,
  format,
  radius = 1,
  area,
  dataType,
}: FormatChartDataParams): ChartData => {
  if (!dataPoint) {
    return {}
  }
  const nationalAvgKeys = NATIONAL_AVG_MAPPINGS[currentDataPoint]
  const nationalAvgData = getNationalAverageData(dataType)
  const nationalAvgPct =
    100 /
    Object.values(nationalAvgKeys)
      .map((key) => nationalAvgData[key as keyof typeof nationalAvgData])
      .reduce((prev, curr) => prev + curr, 0)

  const pct = tableOptions.showCounts ? 1 : percentUnit
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { title, showFloatingTabs, dataInPercentages, ...rows } = format
  const augmentedDataPoint: Record<string, DemoDatum & { nationalAvg?: number }> = {}

  for (const row of Object.values(rows).flat()) {
    if (format.averagesRows?.includes(row)) {
      return {}
    }

    const nationalAvgKey = nationalAvgKeys[row]
    if (row === 'Non Hispanic') {
      augmentedDataPoint[row] = {
        currentYear: 100 - ((dataPoint['% Hispanic'].currentYear as number) || 0),
        fiveYear: 100 - ((dataPoint['% Hispanic'].fiveYear as number) || 0),
        nationalAvg: 100 - (nationalAvgData as Record<string, number>)['cy_hispanic_c'] * nationalAvgPct,
      }
    } else if (row === 'Retail Spend') {
      // Ignore retail spend for chart data since it's the only
      // reference value related to consumer spend we're showing
      // for now.
    } else {
      augmentedDataPoint[row] = {
        currentYear: ((dataPoint[row].currentYear as number) || 0) * pct,
        fiveYear: ((dataPoint[row].fiveYear as number) || 0) * pct,
      }
    }

    if (nationalAvgKey) {
      let natVal = (nationalAvgData as Record<string, number>)[nationalAvgKey]

      if (!tableOptions.showCounts || row === '% Hispanic') {
        natVal = natVal * nationalAvgPct
      } else {
        natVal = area ? getNationalAvgInArea(natVal, area) : getNationalAvgInRadius(natVal, radius)
      }

      augmentedDataPoint[row] = {
        ...augmentedDataPoint[row],
        nationalAvg: natVal,
      }
    }
  }

  return augmentedDataPoint
}
